/**
 * AccountResolutionCard Component
 * Card for resolving a single parsed account with Link/Create/Skip options
 */
import { useState, useCallback, useMemo } from 'react';
import { cn } from '@/utils/cn';
import { ConfidenceBadge } from '../AccountMatcher';
import type { ParsedAccount, AccountResolution, AccountResolutionAction, AccountSuggestion } from '@/types/document';
import type { Account } from '@/types';

export interface AccountResolutionCardProps {
  parsedAccount: ParsedAccount;
  suggestions: AccountSuggestion[];
  existingAccounts: Account[];
  resolution: AccountResolution | null;
  onResolve: (resolution: AccountResolution) => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export function AccountResolutionCard({
  parsedAccount,
  suggestions,
  existingAccounts,
  resolution,
  onResolve,
  isExpanded = false,
  onToggleExpand,
}: AccountResolutionCardProps) {
  const [selectedAction, setSelectedAction] = useState<AccountResolutionAction | null>(
    resolution?.action ?? null
  );
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    resolution?.linkedAccountId ?? parsedAccount.suggestedMatchId
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAccountName, setNewAccountName] = useState(
    `${parsedAccount.institution} ${formatAccountType(parsedAccount.accountType)}${parsedAccount.accountMask ? ` ****${parsedAccount.accountMask}` : ''}`
  );

  // Get the best suggestion
  const bestSuggestion = useMemo(() => {
    if (suggestions.length === 0) return null;
    return suggestions.reduce((best, current) =>
      current.confidence > (best?.confidence ?? 0) ? current : best
    , suggestions[0] ?? null);
  }, [suggestions]);

  // Filter existing accounts by type for the dropdown
  const filteredAccounts = useMemo(() => {
    const accountType = mapParsedTypeToAccountType(parsedAccount.accountType);
    return existingAccounts.filter((a) => a.type === accountType || accountType === 'other');
  }, [existingAccounts, parsedAccount.accountType]);

  // Check if resolved
  const isResolved = resolution !== null;
  const resolutionLabel = getResolutionLabel(resolution, existingAccounts);

  // Handle action selection
  const handleActionSelect = useCallback((action: AccountResolutionAction) => {
    setSelectedAction(action);

    if (action === 'skip') {
      onResolve({
        parsedAccountId: parsedAccount.id,
        action: 'skip',
      });
    } else if (action === 'link' && selectedAccountId) {
      onResolve({
        parsedAccountId: parsedAccount.id,
        action: 'link',
        linkedAccountId: selectedAccountId,
      });
    } else if (action === 'create') {
      setShowCreateForm(true);
    }
  }, [parsedAccount.id, selectedAccountId, onResolve]);

  // Handle account selection for linking
  const handleAccountSelect = useCallback((accountId: string) => {
    setSelectedAccountId(accountId);
    if (selectedAction === 'link') {
      onResolve({
        parsedAccountId: parsedAccount.id,
        action: 'link',
        linkedAccountId: accountId,
      });
    }
  }, [selectedAction, parsedAccount.id, onResolve]);

  // Handle create account confirmation
  const handleCreateConfirm = useCallback(() => {
    if (!newAccountName.trim()) return;

    const accountType = mapParsedTypeToAccountType(parsedAccount.accountType);
    const subtype = mapParsedTypeToSubtype(parsedAccount.accountType, accountType);

    onResolve({
      parsedAccountId: parsedAccount.id,
      action: 'create',
      newAccountData: {
        name: newAccountName.trim(),
        type: accountType,
        subtype,
        institution: parsedAccount.institution,
        mask: parsedAccount.accountMask,
      },
    });
    setShowCreateForm(false);
  }, [newAccountName, parsedAccount, onResolve]);

  return (
    <div
      className={cn(
        'border rounded-lg transition-all',
        isResolved
          ? 'border-green-200 bg-green-50'
          : 'border-gray-200 bg-white',
        isExpanded && 'ring-2 ring-primary-500'
      )}
    >
      {/* Header - always visible */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full p-4 flex items-center justify-between gap-4 text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Status indicator */}
          <div
            className={cn(
              'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
              isResolved ? 'bg-green-100' : 'bg-gray-100'
            )}
          >
            {isResolved ? (
              <CheckIcon className="w-4 h-4 text-green-600" />
            ) : (
              <span className="w-2 h-2 rounded-full bg-gray-400" />
            )}
          </div>

          {/* Account info */}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">
                {parsedAccount.institution}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                {formatAccountType(parsedAccount.accountType)}
              </span>
              {parsedAccount.accountMask && (
                <span className="text-sm text-gray-500">****{parsedAccount.accountMask}</span>
              )}
            </div>
            {isResolved && resolutionLabel && (
              <p className="text-sm text-gray-600 mt-0.5">{resolutionLabel}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Confidence badge for best suggestion */}
          {bestSuggestion && !isResolved && (
            <ConfidenceBadge confidence={bestSuggestion.confidence} showLabel={false} />
          )}

          {/* Transaction count */}
          {parsedAccount.transactionCount !== undefined && (
            <span className="text-sm text-gray-500">
              {parsedAccount.transactionCount} txns
            </span>
          )}

          {/* Expand/collapse icon */}
          <ChevronIcon
            className={cn(
              'w-5 h-5 text-gray-400 transition-transform',
              isExpanded && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200">
          <div className="pt-4 space-y-4">
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <ActionButton
                label="Link to existing"
                icon={<LinkIcon className="w-4 h-4" />}
                selected={selectedAction === 'link'}
                onClick={() => handleActionSelect('link')}
                disabled={filteredAccounts.length === 0}
              />
              <ActionButton
                label="Create new"
                icon={<PlusIcon className="w-4 h-4" />}
                selected={selectedAction === 'create'}
                onClick={() => handleActionSelect('create')}
              />
              <ActionButton
                label="Skip"
                icon={<SkipIcon className="w-4 h-4" />}
                selected={selectedAction === 'skip'}
                onClick={() => handleActionSelect('skip')}
                variant="secondary"
              />
            </div>

            {/* Link to existing - account selection */}
            {selectedAction === 'link' && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select account to link:
                </label>

                {/* Suggestions first */}
                {suggestions.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Suggested matches</p>
                    {suggestions.map((suggestion) => (
                      <AccountSelectOption
                        key={suggestion.accountId}
                        name={suggestion.accountName}
                        mask={suggestion.accountMask}
                        confidence={suggestion.confidence}
                        selected={selectedAccountId === suggestion.accountId}
                        onSelect={() => handleAccountSelect(suggestion.accountId)}
                      />
                    ))}
                  </div>
                )}

                {/* Other accounts dropdown */}
                {filteredAccounts.length > suggestions.length && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Other accounts</p>
                    <select
                      value={selectedAccountId ?? ''}
                      onChange={(e) => handleAccountSelect(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select an account...</option>
                      {filteredAccounts
                        .filter((a) => !suggestions.some((s) => s.accountId === a.id))
                        .map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name} {account.mask ? `****${account.mask}` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {filteredAccounts.length === 0 && (
                  <p className="text-sm text-gray-500 italic">
                    No existing accounts of this type. Create a new one instead.
                  </p>
                )}
              </div>
            )}

            {/* Create new - name input */}
            {showCreateForm && selectedAction === 'create' && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Account name:
                </label>
                <input
                  type="text"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Chase Checking ****1234"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateConfirm}
                    disabled={!newAccountName.trim()}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create
                  </button>
                </div>
              </div>
            )}

            {/* Skip confirmation */}
            {selectedAction === 'skip' && (
              <p className="text-sm text-gray-500 italic">
                Transactions for this account will not be imported.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Action Button Component
// ============================================================================

interface ActionButtonProps {
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
}

function ActionButton({
  label,
  icon,
  selected,
  onClick,
  disabled,
  variant = 'primary',
}: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
        selected
          ? variant === 'secondary'
            ? 'bg-gray-100 text-gray-800 ring-2 ring-gray-400'
            : 'bg-primary-100 text-primary-800 ring-2 ring-primary-500'
          : 'bg-gray-50 text-gray-600 hover:bg-gray-100',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// ============================================================================
// Account Select Option Component
// ============================================================================

interface AccountSelectOptionProps {
  name: string;
  mask?: string;
  confidence?: number;
  selected: boolean;
  onSelect: () => void;
}

function AccountSelectOption({
  name,
  mask,
  confidence,
  selected,
  onSelect,
}: AccountSelectOptionProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full flex items-center justify-between gap-3 p-3 border rounded-lg transition-colors text-left',
        selected
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
          : 'border-gray-200 hover:bg-gray-50'
      )}
      aria-pressed={selected}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={cn(
            'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
            selected ? 'border-primary-600' : 'border-gray-300'
          )}
        >
          {selected && <div className="w-2 h-2 rounded-full bg-primary-600" />}
        </div>
        <span className={cn('font-medium truncate', selected ? 'text-primary-700' : 'text-gray-900')}>
          {name}
        </span>
        {mask && <span className="text-sm text-gray-500 flex-shrink-0">****{mask}</span>}
      </div>
      {confidence !== undefined && <ConfidenceBadge confidence={confidence} showLabel={false} />}
    </button>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatAccountType(type: string): string {
  const typeMap: Record<string, string> = {
    checking: 'Checking',
    savings: 'Savings',
    credit_card: 'Credit Card',
    credit: 'Credit Card',
    investment: 'Investment',
    loan: 'Loan',
    mortgage: 'Mortgage',
    depository: 'Bank Account',
    brokerage: 'Brokerage',
  };
  return typeMap[type?.toLowerCase() ?? ''] ?? (type?.charAt(0)?.toUpperCase() ?? '') + (type?.slice(1) ?? '');
}

function mapParsedTypeToAccountType(parsedType: string): string {
  const normalized = parsedType?.toLowerCase() ?? '';
  if (normalized.includes('checking') || normalized.includes('savings') || normalized.includes('depository')) {
    return 'depository';
  }
  if (normalized.includes('credit')) return 'credit';
  if (normalized.includes('loan') || normalized.includes('mortgage')) return 'loan';
  if (normalized.includes('invest') || normalized.includes('brokerage')) return 'investment';
  return 'other';
}

function mapParsedTypeToSubtype(parsedType: string, accountType: string): string {
  const normalized = parsedType?.toLowerCase() ?? '';
  if (normalized.includes('checking')) return 'checking';
  if (normalized.includes('savings')) return 'savings';
  if (normalized.includes('credit')) return 'credit_card';
  if (normalized.includes('mortgage')) return 'mortgage';
  if (accountType === 'credit') return 'credit_card';
  if (accountType === 'depository') return 'checking';
  return 'other';
}

function getResolutionLabel(resolution: AccountResolution | null, accounts: Account[]): string | null {
  if (!resolution) return null;

  switch (resolution.action) {
    case 'link': {
      const account = accounts.find((a) => a.id === resolution.linkedAccountId);
      return account ? `Linked to: ${account.name}` : 'Linked to existing account';
    }
    case 'create':
      return `Will create: ${resolution.newAccountData?.name ?? 'New account'}`;
    case 'skip':
      return 'Skipped';
    default:
      return null;
  }
}

// ============================================================================
// Icon Components
// ============================================================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function SkipIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062A1.125 1.125 0 013 16.81V8.688zM12.75 8.688c0-.864.933-1.405 1.683-.977l7.108 4.062a1.125 1.125 0 010 1.953l-7.108 4.062a1.125 1.125 0 01-1.683-.977V8.688z" />
    </svg>
  );
}

export default AccountResolutionCard;
